import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/utils/utils';

// Ajout de logs pour le débogage des interactions du Popover
const Popover = ({ open, onOpenChange, children, ...props }: PopoverPrimitive.PopoverProps) => {
  console.log("Popover rendering, open state:", open);
  
  // Wrapper personnalisé pour onOpenChange pour ajouter des logs
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    console.log("Popover state changing from", open, "to", newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  }, [open, onOpenChange]);
  
  return (
    <PopoverPrimitive.Root 
      open={open}
      onOpenChange={handleOpenChange}
      {...props}
    >
      {children}
    </PopoverPrimitive.Root>
  );
};

// Ajout de logs pour le débogage des interactions du trigger
const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ onClick, ...props }, ref) => {
  // Wrapper pour onClick pour ajouter des logs
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    console.log("PopoverTrigger clicked");
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);

  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      onClick={handleClick}
      {...props}
    />
  );
});
PopoverTrigger.displayName = 'PopoverTrigger';

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => {
  console.log("PopoverContent rendering with props:", props);
  
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        onInteractOutside={(event) => {
          console.log("Interaction outside PopoverContent detected");
          // Original onInteractOutside props will still be called if provided
          if (props.onInteractOutside) {
            props.onInteractOutside(event);
          }
        }}
        onEscapeKeyDown={() => {
          console.log("Escape key pressed in PopoverContent");
        }}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
